import type { CallsMediaStripProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsMediaStrip/index.types';
import {
  byThreshold,
  toneForBitrate,
  toneForIce,
} from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsMediaStrip/utils';
import { useCallRtpStats } from 'Modules/softphone/components/SoftphoneDevTools/hooks';
import type { DevToolsTone } from 'Modules/softphone/components/SoftphoneDevTools/index.types';
import { TONE } from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React from 'react';
import { cn } from '~/lib/utils';

const Reading: React.FC<{
  label: string;
  value: string;
  tone: DevToolsTone;
}> = ({ label, value, tone }) => (
  <span>
    {label} <span className={TONE[tone].text}>{value}</span>
  </span>
);

/**
 * CallsMediaStrip - Is anything flowing, and is it arriving intact
 *
 * The readings a no-audio report is triaged with, on one line under the
 * call.
 */
export const CallsMediaStrip: React.FC<CallsMediaStripProps> = ({
  callId,
  isMuted,
}) => {
  const {
    isMonitored,
    iceConnectionState,
    sendKbps,
    receiveKbps,
    lossPercent,
    jitterMs,
    roundTripMs,
  } = useCallRtpStats(callId);

  return (
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 px-3 pb-2.5 font-sp-mono text-[10.5px] text-zinc-400">
      <Reading
        label="ICE"
        value={iceConnectionState ?? 'no media path'}
        tone={toneForIce(iceConnectionState)}
      />

      {sendKbps !== null && receiveKbps !== null && (
        <span>
          RTP{' '}
          <span className={TONE[toneForBitrate(sendKbps, iceConnectionState)].text}>
            ↑{sendKbps}k
          </span>{' '}
          <span
            className={TONE[toneForBitrate(receiveKbps, iceConnectionState)].text}
          >
            ↓{receiveKbps}k
          </span>
        </span>
      )}

      {lossPercent !== null && (
        <Reading
          label="loss"
          value={`${lossPercent.toFixed(1)}%`}
          tone={byThreshold(lossPercent, 1, 5)}
        />
      )}

      {jitterMs !== null && (
        <Reading
          label="jitter"
          value={`${jitterMs}ms`}
          tone={byThreshold(jitterMs, 30, 50)}
        />
      )}

      {roundTripMs !== null && (
        <Reading
          label="rtt"
          value={`${roundTripMs}ms`}
          tone={byThreshold(roundTripMs, 150, 300)}
        />
      )}

      <span className={cn(isMuted ? TONE.warn.text : TONE.ok.text)}>
        {isMuted ? 'Muted' : 'Audio On'}
      </span>

      {!isMonitored && (
        // Quality collection follows the current call only, and stops for a
        // conference participant once its own peer connection is torn down.
        // Saying nothing here would read as "zero loss".
        <span className="text-zinc-400">not monitored</span>
      )}
    </div>
  );
};
